import React, { useEffect } from "react";
import { motion } from "framer-motion";

const About = () => {
  useEffect(() => {
    document.title = "About Us - Gujarat State APP Association 2025";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Learn about the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Our mission, vision, and commitment to legal excellence in Gujarat. GSAPPPA is a State Level Legal Association dedicated to Prosecutors Welfare Association and Legal Professionals Association Gujarat.";
    }
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = "https://gsappa.kevalontechnology.in/about";
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = 'https://gsappa.kevalontechnology.in/about';
      document.head.appendChild(canonical);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = "About Us - Gujarat State APP Association 2025";
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.content = "Learn about the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Our mission, vision, and commitment to legal excellence in Gujarat. GSAPPPA is a State Level Legal Association dedicated to Prosecutors Welfare Association and Legal Professionals Association Gujarat.";
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = "https://gsappa.kevalontechnology.in/about";
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.content = "About Us - Gujarat State APP Association 2025";
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) twitterDescription.content = "Learn about the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Our mission, vision, and commitment to legal excellence in Gujarat. GSAPPPA is a State Level Legal Association dedicated to Prosecutors Welfare Association and Legal Professionals Association Gujarat.";
    
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) twitterUrl.content = "https://gsappa.kevalontechnology.in/about";
  }, []);
  // Reusable animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.9 } },
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, ease: "easeOut" },
    },
  };

  const cardContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.25 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const objectives = [
    "Safeguard the service conditions, legal rights, independence, and professional dignity of Assistant Public Prosecutors across Gujarat.",
    "Promote continuous legal education, advanced prosecution techniques, and adherence to the highest ethical standards in public prosecution.",
    "Act as an effective collective voice and bridge between Assistant Public Prosecutors, the Government, Judiciary, and other stakeholders.",
    "Strengthen coordination, mutual trust, and efficient functioning within the criminal justice delivery system in the state.",
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      className="bg-gradient-to-b from-gray-50 to-white text-gray-800 min-h-screen"
    >
      {/* Hero Section */}
      <motion.section
        initial={{ scale: 1.03, opacity: 0.9 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        className="relative bg-gradient-to-r from-[#0B0B0B] via-[#1A1A1A] to-[#0B0B0B] text-white py-40 md:py-48 text-center overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.h1
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 1.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-[#D4A017] tracking-tight"
          >
            About Us
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="mt-6 text-xl md:text-2xl lg:text-3xl text-gray-200 font-medium"
          >
            THE GUJARAT STATE ASSISTANT PUBLIC PROSECUTORS ASSOCIATION – 2025
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-5 text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Committed to justice, integrity, and professional excellence
          </motion.p>
        </div>
      </motion.section>

      {/* Who We Are */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="max-w-6xl mx-auto px-6 py-20 md:py-24"
      >
        <div className="w-20 h-1 bg-[#D4A017] mx-auto mb-10 rounded-full" />

        <motion.h2
          className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-center text-gray-900 mb-10 tracking-tight"
        >
          Who We Are
        </motion.h2>

        <motion.p
          className="text-lg md:text-xl leading-relaxed text-gray-700 text-center max-w-4xl mx-auto"
        >
          The THE GUJARAT STATE ASSISTANT PUBLIC PROSECUTORS ASSOCIATION – 2025 (GSAPPA) is the recognized professional body representing Assistant Public Prosecutors serving across the courts of Gujarat. 
          Established to protect the dignity, independence, and welfare of its members, the Association works tirelessly to strengthen the criminal justice system, promote ethical prosecution practices, and foster collaboration between prosecutors, judiciary, and government authorities.
        </motion.p>
      </motion.section>

      {/* Role of Prosecutors */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="max-w-6xl mx-auto px-6 py-20 md:py-24"
      >
        <div className="w-20 h-1 bg-[#D4A017] mx-auto mb-10 rounded-full" />

        <motion.h2
          className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-center text-gray-900 mb-10 tracking-tight"
        >
          Role of Prosecutors in the Criminal Justice System of India
        </motion.h2>

        <motion.p
          className="text-lg md:text-xl leading-relaxed text-gray-700 text-center max-w-4xl mx-auto"
        >
          In the Indian criminal justice system, the prosecution of offences in trials is preceded by the investigation of those particular offences by the police. There are other minor offences as well which are prosecuted by private parties; these are known as non-cognizable offences. Investigation of crimes is the domain of the police. For this purpose, the police have been given the power to arrest person(s), search premises of the crime scene, seize documents or items related to the crime, and exercise other varied powers provided in the Code of Criminal Procedure to aid in the investigation of a crime.  <br />
        </motion.p>

        <motion.p
          className="text-lg md:text-xl leading-relaxed text-gray-700 text-center max-w-4xl mx-auto"
        >
          It is a common assumption that the work of a prosecutor begins after the police have completed the investigation and the chargesheet is filed. Most judgments of various High Courts and the Supreme Court have commented on the conduct of a prosecutor concerning trial and the ethics that a prosecutor should adhere to. These developments induce a prosecutor to conduct herself like another judge in the court. A prosecutor is the centerpiece of all trials in a court and has an important role in justice delivery to the victims. Let us look at the various trial and pre-trial stages where the prosecutor plays a crucial role.
        </motion.p>
      </motion.section>

      {/* Vision & Mission */}
      <section className="bg-gray-50 py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            variants={cardContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid md:grid-cols-2 gap-10 lg:gap-12"
          >
            <motion.div
              variants={cardVariants}
              whileHover={{ y: -12, boxShadow: "0 25px 50px rgba(0,0,0,0.12)" }}
              className="bg-white p-10 md:p-12 rounded-3xl shadow-xl border-t-4 border-[#D4A017] transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#D4A017]/10 flex items-center justify-center text-3xl font-bold text-[#D4A017]">
                  V
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
To build an independent, impartial, and fearless prosecution system that delivers justice with integrity, fairness, and transparency, ensuring equal application of law and strengthening public confidence in the criminal justice system.              </p>
            </motion.div>

            <motion.div
              variants={cardVariants}
              whileHover={{ y: -12, boxShadow: "0 25px 50px rgba(0,0,0,0.12)" }}
              className="bg-white p-10 md:p-12 rounded-3xl shadow-xl border-t-4 border-[#D4A017] transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#D4A017]/10 flex items-center justify-center text-3xl font-bold text-[#D4A017]">
                  M
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
To empower Assistant Public Prosecutors with advanced training, ethical strength, and collective voice, enabling impartial prosecution and the fearless delivery of justice.              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Objectives */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="max-w-6xl mx-auto px-6 py-20 md:py-24 bg-gradient-to-b from-white to-gray-50/50"
      >
        <div className="w-20 h-1 bg-[#D4A017] mx-auto mb-10 rounded-full" />

        <motion.h2
          className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-center text-gray-900 mb-12 tracking-tight"
        >
          Our Core Objectives
        </motion.h2>

        <div className="grid gap-6 md:gap-8 max-w-4xl mx-auto">
          {objectives.map((obj, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.7 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden flex items-start group hover:shadow-xl hover:border-[#D4A017]/40 transition-all duration-300"
            >
              <div className="bg-[#D4A017] text-white min-w-[70px] flex items-center justify-center px-5 py-8 font-bold text-3xl md:text-4xl">
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="p-7 md:p-9 flex-1">
                <p className="text-gray-800 text-lg md:text-xl leading-relaxed">
                  {obj}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CTA Footer */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.3 }}
        viewport={{ once: true }}
        className="bg-gradient-to-r from-[#0B0B0B] to-black py-20 md:py-24 text-center text-white"
      >
        <motion.h3
          initial={{ y: 40 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.9 }}
          className="text-3xl md:text-5xl font-bold mb-6"
        >
          Dedicated to the Service of Justice
        </motion.h3>
        <motion.p
          initial={{ y: 40 }}
          whileInView={{ y: 0 }}
          transition={{ delay: 0.3, duration: 0.9 }}
          className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto px-6"
        >
          Upholding integrity, professionalism, and the rule of law across Gujarat.
        </motion.p>
      </motion.section>
    </motion.div>
  );
};

export default About;